import dayjs from 'dayjs';
import { action, computed, observable, runInAction } from 'mobx';
import type { NavigateFunction } from 'react-router';
import { getAppConfig } from '../config/app';
import { storageService } from '../services/storage.service';
import { initializeUnleashClient } from '../services/unleash-feature-flags.service';
import { AppConfig } from '../types/app-config';
import {
  AgentData,
  AuditStatus,
  isUpgradeScheduled,
  PracticeConfig,
  UpgradeStatus,
} from '../types/practice-types';
import type { Sap } from '../types/users-types';
import { isServerAgent } from '../utils/agents-utils';
import { allowedUpgradeEventInterval } from '../utils/date-utils';
import EventIntervalsHandler from '../utils/event-intervals-handler';
import { isDefined, sortByField } from '../utils/object-utils';
import AbstractStore from './AbstractStore';
import { PRACTICE_PATH } from './constants';
import RootStore from './RootStore';

const LOGGING_OUT = 'LOGGING_OUT';

const TIME_INTERVAL_MINUTES = 30;
const TIMEOUT_SHIFT_MINUTES = 5;

export default class GlobalStore extends AbstractStore {
  private upgradeTaskPopupTimeoutHandle: ReturnType<typeof setTimeout> | undefined = undefined;

  private upgradeTaskTimeoutHandle: ReturnType<typeof setTimeout> | undefined = undefined;

  public constructor(rootStore: RootStore) {
    super(rootStore);
    this._loggingOut = storageService.getItem(LOGGING_OUT, true) || false;
  }

  @observable accessor appConfig: AppConfig | undefined;

  @observable accessor _sapIdChanged = true;

  @observable accessor selectedSap: Sap = { id: '', name: '' };

  @observable accessor practiceSelectorDisabled = false;

  @observable accessor isLoggedIn = false;

  @observable accessor isLoading = false;

  @observable accessor practice: PracticeConfig | undefined;

  @observable accessor agentsData: AgentData[] = [];

  @observable accessor eventIntervalsHandler: EventIntervalsHandler = new EventIntervalsHandler(
    [],
    TIME_INTERVAL_MINUTES,
  );

  @observable accessor contactsChanged = false;

  @observable accessor leftNavPanelShown = false;

  @observable private accessor _loggingOut = false;

  @computed
  public get loggingOut(): boolean {
    return this._loggingOut;
  }

  @computed
  public get userSaps(): Sap[] {
    return sortByField(this.root.apiStore.user?.saps || [], 'id');
  }

  @computed
  public get scheduleUpgradeTimeSet(): boolean {
    return (
      isDefined(this.practice?.scheduleUpgradeTimestamp) && isDefined(this.practice?.utcOffset)
    );
  }

  @computed
  public get upgradeScheduled(): boolean {
    return isUpgradeScheduled(this.practice);
  }

  @computed
  public get serverAgentsToUpgrade(): AgentData[] {
    return this.agentsData.filter(d => isServerAgent(d) && d.status === UpgradeStatus.UPGRADE);
  }

  @computed
  public get agentsReady(): boolean {
    if (this.agentsData.length === 0) return false;
    if (this.serverAgentsToUpgrade.length !== 1) return false;
    return this.agentsData.every(
      d => d.status !== UpgradeStatus.UPGRADE || d.auditStatus === AuditStatus.QUALIFIED,
    );
  }

  public get navigate(): NavigateFunction {
    return this._navigate;
  }

  public set loggingOut(value: boolean) {
    this._loggingOut = value;
    storageService.replace(LOGGING_OUT, value, true);
  }

  @action
  public changeSelectedSap(sapId: string, clear = true): void {
    if (this.selectedSap.id !== sapId) {
      this.sapIdChanged = true;
      const sap = this.userSaps.find(us => us.id === sapId);
      this.selectedSap = sap ? sap : { id: '', name: '' };
    } else if (clear) {
      this.selectedSap = { id: '', name: '' };
    }
  }

  @action
  public setLoading(value: boolean): void {
    this.isLoading = value;
  }

  @action
  public async fetchEventIntervals(): Promise<EventIntervalsHandler | undefined> {
    try {
      const allowedInterval = allowedUpgradeEventInterval();
      const events = await this.root.apiStore.findScheduledMaintenancesBetweenDates(
        allowedInterval.start,
        allowedInterval.end,
      );
      runInAction(
        () =>
          (this.eventIntervalsHandler = new EventIntervalsHandler(
            events.map(e => ({ start: e.startDateTime, end: e.endDateTime })),
            TIME_INTERVAL_MINUTES,
          )),
      );
      this.eventIntervalsHandler.setAllowedInterval(allowedInterval);
      return this.eventIntervalsHandler;
    } catch (e: any) {
      alert(e.toString());
    }
  }

  @action
  public async fetchPracticeData(withAgents = true, initialCheck = false): Promise<void> {
    const practice = await this.root.apiStore.getPractice(this.sapId, initialCheck);
    const agentsData = withAgents
      ? await this.root.apiStore.getCachedAgents(this.sapId)
      : this.agentsData;
    runInAction(() => {
      this.practice = practice;
      this.agentsData = agentsData;
      this.contactsChanged = false;
    });
  }

  public async init(navigate: NavigateFunction): Promise<void> {
    runInAction(() => (this._navigate = navigate));
    const appConfig = await getAppConfig();
    if (appConfig) {
      runInAction(() => (this.appConfig = appConfig));
      await initializeUnleashClient({
        appName: this.appConfig!.unleashFeatureFlagsAppName,
        clientKey: this.appConfig!.unleashFeatureFlagsClientKey,
        url: this.appConfig!.unleashFeatureFlagsUrl,
        context: { userId: this.root.apiStore.userEmail, sessionId: this.root.apiStore.userGuid },
        refreshInterval: 60,
      });
    }
  }

  public setUpgradeTaskSessionEndTimeouts(): void {
    this.clearUpgradeTaskSessionEndTimeouts();
    if (this.practice?.isLocked && this.practice.lockedByEmail === this.root.apiStore.userEmail) {
      const endTime = dayjs(this.practice.lockEndTimestamp);
      let timeoutMs = endTime.add(-TIMEOUT_SHIFT_MINUTES, 'minutes').diff(dayjs(), 'ms');
      if (timeoutMs < 1000) timeoutMs = 1000;
      this.upgradeTaskPopupTimeoutHandle = setTimeout(
        () => this.root.globalModalStore.showSessionEndingPopup(),
        timeoutMs,
      );
      this.upgradeTaskTimeoutHandle = setTimeout(
        () => void this.navigate('/'),
        endTime.diff(dayjs(), 'ms'),
      );
      console.log(`Upgrade task session timeout is set to ${timeoutMs / 1000}s`);
    }
  }

  public clearUpgradeTaskSessionEndTimeouts(): void {
    if (this.upgradeTaskPopupTimeoutHandle) {
      clearTimeout(this.upgradeTaskPopupTimeoutHandle);
      this.upgradeTaskPopupTimeoutHandle = undefined;
      clearTimeout(this.upgradeTaskTimeoutHandle);
      this.upgradeTaskTimeoutHandle = undefined;
      console.log('Upgrade task session timeout is cleared');
    }
  }

  public async extendUpgradeTaskSessionLock(): Promise<void> {
    const practice = await this.root.apiStore.backendPost(PRACTICE_PATH, {
      action: 'EXTEND_SCHEDULE_UPGRADE_PROCESS',
      sapId: this.sapId,
    });
    runInAction(() => (this.practice = practice));
    this.setUpgradeTaskSessionEndTimeouts();
  }

  public async endUpgradeTaskSessionIfStarted(): Promise<void> {
    this.clearUpgradeTaskSessionEndTimeouts();
    if (this.selectedSap.id !== '' && this.practice?.isLocked) {
      console.log(`Unlocking upgrade task session for sapId=${this.selectedSap.id}`);
      await this.root.apiStore.backendPost(PRACTICE_PATH, {
        action: 'EXIT_SCHEDULE_UPGRADE_PROCESS',
        sapId: this.selectedSap.id,
      });
      runInAction(() => {
        this.isLoggedIn = false;
        this.root.globalModalStore.closeGlobalModal();
      });
    }
  }

  public async signOut(): Promise<void> {
    this.loggingOut = true;
    await this.endUpgradeTaskSessionIfStarted();

    for (const storeName of Object.keys(this.root)) {
      const store = this.root[storeName] as AbstractStore;
      if (!(store instanceof GlobalStore)) await store.signOut();
    }
  }

  public onChangeSapId(): void {
    for (const storeName of Object.keys(this.root)) {
      const store = this.root[storeName] as AbstractStore;
      if (!(store instanceof GlobalStore)) store.onChangeSapId();
    }
  }

  private _navigate: NavigateFunction = () => {};
}
